import * as React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { Config, FORM_ERROR, ValidationErrors } from 'final-form';
import { FunctionComponent, useMemo } from 'react';
import { DeepPartial } from '../common/props';
import { ModalComponentProps } from '../modal/ModalManager';

/**
 * Je zde aby nemusela být závislost na RFF.
 */
const RFF_ERROR = 'FINAL_FORM/form-error';

/**
 * React final form validační chyba.
 */
type RffValidationErrors =
  | {
      _exception?: string;
      [RFF_ERROR]?: string;
    }
  | any
  | null;

/**
 * Nastavení submit errors do react final formuláře.
 * Jedná se o mutator pro react final form.
 *
 * @param errors
 * @param state
 * @param tools
 */
// @ts-ignore
export const setFormSubmitErrors = ([errors], state, tools) => {
  let newState: any = state;

  // Submit errors
  newState = tools.setIn(newState, 'formState.submitErrors', errors) as any;
  newState = tools.setIn(
    newState,
    'formState.submitError',
    errors[FORM_ERROR]
  ) as any;

  // All touched
  const newFields: any = {};
  Object.keys(state.fields).forEach((key) => {
    newFields[key] = { ...state.fields[key], touched: true };
  });
  newState = tools.setIn(newState, 'fields', newFields) as any;

  // Promítnutí do state
  state.formState = newState.formState;
  state.fields = newState.fields;
};

/**
 * Nastavení  errors do react final formuláře.
 * Jedná se o mutator pro react final form.
 *
 * @param errors
 * @param state
 * @param tools
 */
// @ts-ignore
export const setFormErrors = ([errors], state, tools) => {
  let newState: any = state;

  // Submit errors
  newState = tools.setIn(newState, 'formState.errors', errors) as any;
  newState = tools.setIn(
    newState,
    'formState.error',
    errors[FORM_ERROR]
  ) as any;

  // All touched
  const newFields: any = {};
  Object.keys(state.fields).forEach((key) => {
    newFields[key] = { ...state.fields[key], touched: true };
  });
  newState = tools.setIn(newState, 'fields', newFields) as any;

  // Promítnutí do state
  state.formState = newState.formState;
  state.fields = newState.fields;
};

// ----------

type InnerFormProps<FORM_DATA> = ModalComponentProps & {
  formRenderProps: FormRenderProps<FORM_DATA>;
};

type FormCompProps<FORM_DATA, PROPS> = {
  onSubmit: (data: FORM_DATA) => Promise<RffValidationErrors> | Promise<void>;
  initialValues?: DeepPartial<FORM_DATA>;
} & ModalComponentProps &
  Omit<PROPS, 'formRenderProps' | 'onClose' | 'children'>;

type StandardFormProps<FORM_DATA, PROPS> = ModalComponentProps & {
  form: FunctionComponent<InnerFormProps<FORM_DATA>>;
  formProps?: Omit<
    Config<FORM_DATA, DeepPartial<FORM_DATA>>,
    'onSubmit' | 'initialValues' | 'form' | 'validate'
  > & {
    validate?: (
      values: FORM_DATA,
      // Vím, že by to mělo být jako Omit<PROPS, 'formRenderProps' | 'onClose'>, ale z hlediska jednodušší definice při používání jsem sem dal PROPS 🙈👐🤷‍♂️
      props: PROPS
    ) => ValidationErrors | Promise<ValidationErrors>;
  };
} & FormCompProps<FORM_DATA, PROPS>;

type StandardFormProps2<FORM_DATA> = ModalComponentProps & {
  formInitialValues?: DeepPartial<FORM_DATA>;
};

const DialogForm = <FORM_DATA, PROPS>({
  formInitialValues,
  initialValues,
  onClose,
  onSubmit,
  formProps,
  form,
  ...props
}: StandardFormProps<FORM_DATA, PROPS> & StandardFormProps2<FORM_DATA>) => {
  const initValues = useMemo(() => {
    return {
      ...formInitialValues,
      ...initialValues,
    };
  }, []);

  const { validate, ...restFormProps } = formProps || {};

  const validateWithProps = useMemo(() => {
    if (typeof validate === 'function') {
      return async (values: FORM_DATA) => {
        // @ts-ignore
        return await validate(values, props ?? {});
      };
    } else {
      return validate;
    }
  }, [validate, props]);

  return (
    // @ts-ignore
    <Form<FORM_DATA>
      initialValues={initValues}
      {...restFormProps}
      validate={validateWithProps}
      onSubmit={async (data) => {
        return await onSubmit(data);
      }}
    >
      {(rffFormProps) =>
        React.createElement(form, {
          ...props,
          formRenderProps: rffFormProps,
          onClose,
        })
      }
    </Form>
  );
};

export const isErrorShown = (errors: any, touched: any) => {
  // Uděláme průnik klíčů, jestli nějaký je, tak vrátíme true
  return Object.keys(errors).some((key) => touched[key]);
};

export function modalFormDialog<FORM_DATA, PROPS>(
  form: FunctionComponent<InnerFormProps<FORM_DATA> & PROPS>,
  formProps?: Omit<
    Config<FORM_DATA, DeepPartial<FORM_DATA>>,
    'onSubmit' | 'initialValues' | 'form' | 'validate'
  > & {
    validate?: (
      values: FORM_DATA,
      // Vím, že by to mělo být jako Omit<PROPS, 'formRenderProps' | 'onClose'>, ale z hlediska jednodušší definice při používání jsem sem dal PROPS 🙈👐🤷‍♂️
      props: PROPS
    ) => ValidationErrors | Promise<ValidationErrors>;
  },
  // Static initial values které udává už rovnou formulář jako takový, lze pak překrýt initialValues při volání modalu
  initialValues?: FORM_DATA
) {
  return (props: FormCompProps<FORM_DATA, PROPS>) => (
    // @ts-ignore
    <DialogForm
      // @ts-ignore
      form={form}
      formInitialValues={initialValues}
      formProps={formProps}
      {...props}
    />
  );
}

// @ts-ignore
export const _setSubmitErrors = ([errors], state, tools) => {
  let newState: any = state;

  errors = errors || {};

  // Submit errors
  newState = tools.setIn(newState, 'formState.submitErrors', errors);
  newState = tools.setIn(newState, 'formState.submitError', errors[FORM_ERROR]);

  // All touched
  const newFields: any = {};
  Object.keys(state.fields).forEach((key) => {
    newFields[key] = { ...state.fields[key], touched: true };
  });
  newState = tools.setIn(newState, 'fields', newFields);

  // Promítnutí do state
  state.formState = newState.formState;
  state.fields = newState.fields;
};

// @ts-ignore
export const _setErrors = ([errors], state, tools, rest) => {
  let newState: any = state;

  errors = errors || {};

  // Submit errors
  newState = tools.setIn(newState, 'formState.errors', errors);
  newState = tools.setIn(newState, 'formState.error', errors[FORM_ERROR]);

  // All touched
  const newFields: any = {};
  Object.keys(state.fields).forEach((key) => {
    newFields[key] = { ...state.fields[key], touched: true };
  });
  newState = tools.setIn(newState, 'fields', newFields);

  // Promítnutí do state
  state.formState = newState.formState;
  state.fields = newState.fields;
};

// @ts-ignore
export const setLastSubmittedValues = ([values], state, tools) => {
  let newState: any = state;
  newState = tools.setIn(newState, 'formState.lastSubmittedValues', values);

  // Promítnutí do state
  state.formState = newState.formState;
};

/**
 * Nastavení submit errors do react final form formuláře - touched jen na submit error fields, na ostatní vynuluje.
 * @param errors
 */
// @ts-ignore
export const setSubmitErrorsOnly = ([errors], state, tools) => {
  let newState: any = state;

  errors = errors || {};

  // Submit errors
  newState = tools.setIn(newState, 'formState.submitErrors', errors);
  newState = tools.setIn(newState, 'formState.submitError', errors[FORM_ERROR]);

  // Touched jen na errors, ostatní non touched
  const newFields: any = getFieldsWithTouchedByErrors(state.fields, errors);
  newState = tools.setIn(newState, 'fields', newFields);

  // Promítnutí do state
  state.formState = newState.formState;
  state.fields = newState.fields;
};

const fillErrorFields = (
  errorFields: Set<string>,
  errors: any,
  prefix?: string
) => {
  Object.keys(errors).forEach((key) => {
    const value = errors[key];
    if (value !== null && value !== undefined) {
      if (typeof value === 'object') {
        fillErrorFields(errorFields, value, prefix ? `${prefix}.${key}` : key);
      } else {
        errorFields.add(prefix ? `${prefix}.${key}` : key);
      }
    }
  });
};

const getFieldsWithTouchedByErrors = (stateFields: any[], errors: any): any => {
  // Seznam error fields, např. ['aaa', 'bbb', 'ccc', 'dokument.nazev', polozky[0].nazev]
  const errorFields = new Set<string>();
  fillErrorFields(errorFields, errors, undefined);

  const newFields: any = {};
  Object.keys(stateFields).forEach((key) => {
    if (errorFields.has(key)) {
      // @ts-ignore
      newFields[key] = { ...stateFields[key], touched: true };
    } else {
      // @ts-ignore
      newFields[key] = { ...stateFields[key], touched: false };
    }
  });

  return newFields;
};

/**
 * Nastavení errors do react final form formuláře - touched jen na error fields, na ostatní vynuluje.
 * @param errors
 */
// @ts-ignore
export const setErrorsOnly = ([errors], state, tools) => {
  let newState: any = state;

  errors = errors || {};

  // Submit errors
  newState = tools.setIn(newState, 'formState.errors', errors);
  newState = tools.setIn(newState, 'formState.error', errors[FORM_ERROR]);

  // Touched jen na errors, ostatní non touched
  const newFields: any = getFieldsWithTouchedByErrors(state.fields, errors);
  newState = tools.setIn(newState, 'fields', newFields);

  // Promítnutí do state
  state.formState = newState.formState;
  state.fields = newState.fields;
};

/**
 * Test zda na poli bylo touched nebo modified.
 * @param prefix fieldů
 * @param touched
 * @param modified
 */
export const formHasTouchedModified = (
  prefix: string,
  touched: any,
  modified: any
): boolean => {
  const keys1 = Object.keys(touched);
  for (const key of keys1) {
    if (key.startsWith(prefix) && touched[key]) {
      return true;
    }
  }
  const keys2 = Object.keys(modified);
  for (const key of keys2) {
    if (key.startsWith(prefix) && modified[key]) {
      return true;
    }
  }
  return false;
};
